import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Activity, Calendar, CheckCircle2, ClipboardList, ChevronRight,
  Users2, Radio, Wrench, AlertTriangle, MapPin, MessageSquare,
  Bell, Search, ShieldCheck, User,
} from "lucide-react";

/* ============================================================================
   CompanyDashboard — v2, mirrors the "Welcome back, Michael" reference mockup.
   Sections: Greeting · 4 stat cards · Active + Planned deployment columns ·
   Bottom stats strip · Emergency alert.
   ========================================================================== */
export default function CompanyDashboard() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: jobsData }, { data: bookingsData }] = await Promise.all([
          api.get("/jobs").catch(() => ({ data: [] })),
          api.get("/bookings").catch(() => ({ data: [] })),
        ]);
        setJobs(jobsData || []);
        setBookings(bookingsData || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const firstName = (user?.full_name || user?.company_name || "there").split(" ")[0];

  // Slice the data into deployment buckets.
  const now = new Date();
  const isActive  = (b) => b.status === "in_progress" || (b.start_at && new Date(b.start_at) <= now && (!b.end_at || new Date(b.end_at) >= now));
  const isPlanned = (b) => (b.status === "confirmed" || b.status === "unlocked") && b.start_at && new Date(b.start_at) > now;
  const isDoneToday = (b) => b.status === "completed" && b.end_at && new Date(b.end_at).toDateString() === now.toDateString();

  const activeDeployments  = bookings.filter(isActive);
  const plannedDeployments = bookings.filter(isPlanned);
  const completedToday     = bookings.filter(isDoneToday);
  const totalTasks         = jobs.length + bookings.length;

  const stats = [
    { key: "active",    label: "Active Deployments",  value: activeDeployments.length,  sub: "In Progress",  Icon: Activity,       tone: "brand" },
    { key: "planned",   label: "Planned Deployments", value: plannedDeployments.length, sub: "Upcoming",     Icon: Calendar,       tone: "violet" },
    { key: "done",      label: "Completed Today",     value: completedToday.length,     sub: "Deployments",  Icon: CheckCircle2,   tone: "gold" },
    { key: "tasks",     label: "Total Tasks",         value: totalTasks,                sub: "Across All Jobs", Icon: ClipboardList, tone: "amber" },
  ];

  return (
    <div className="space-y-6" data-testid="company-dashboard">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-outfit font-bold text-white leading-none">Welcome back, {firstName}</h1>
        <p className="mt-1.5 text-white/60 font-manrope text-sm">Here's what's happening with your deployments today.</p>
      </div>

      {/* 4 stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <StatCard key={s.key} {...s} />
        ))}
      </div>

      {/* Two-column: Active + Planned */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ActiveDeploymentsCard items={activeDeployments} loading={loading} />
        <PlannedDeploymentsCard items={plannedDeployments} loading={loading} />
      </div>

      {/* Bottom strip: quick stats + emergency */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <QuickStat Icon={Radio}          label="Live Rooms"       value={activeDeployments.length}          sub="Active Now"   tone="cyan" />
        <QuickStat Icon={Users2}         label="Teams On Duty"    value={activeDeployments.reduce((n, b) => n + (b.workers_needed || 1), 0)} sub="Deployed"     tone="brand" />
        <QuickStat Icon={MessageSquare}  label="Pending Requests" value={jobs.filter(j => j.status === "pending_review").length}  sub="Requires Action" tone="amber" />
        <QuickStat Icon={Wrench}         label="Maintenance"      value={0} sub="Scheduled"    tone="violet" />
        <EmergencyAlert />
      </div>
    </div>
  );
}

/* ============================================================================
   Sub-components
   ========================================================================== */

function StatCard({ label, value, sub, Icon, tone }) {
  const tones = {
    brand:  { bg: "bg-brand/15",      border: "border-brand/40",      icon: "text-brand" },
    violet: { bg: "bg-violet-500/15", border: "border-violet-500/40", icon: "text-violet-300" },
    gold:   { bg: "bg-gold-500/15",   border: "border-gold-500/40",   icon: "text-gold-500" },
    amber:  { bg: "bg-amber-500/15",  border: "border-amber-500/40",  icon: "text-amber-400" },
    cyan:   { bg: "bg-cyan-500/15",   border: "border-cyan-500/40",   icon: "text-cyan-300" },
  }[tone] || { bg: "bg-white/10", border: "border-white/20", icon: "text-white" };
  return (
    <div className="rounded-2xl bg-[#141414] border border-white/10 p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${tones.bg} border ${tones.border} ${tones.icon}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-white/50 font-outfit font-bold leading-tight">{label}</div>
        <div className="text-2xl font-outfit font-bold text-white leading-tight mt-0.5">{value}</div>
        <div className="text-[11px] text-white/50 font-manrope leading-tight">{sub}</div>
      </div>
    </div>
  );
}

function ProgressRing({ value, tone }) {
  const c = { brand: "#FF7200", gold: "#D4A24C", amber: "#F59E0B", red: "#F87171", violet: "#C084FC" }[tone] || "#FF7200";
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="relative w-14 h-14 flex-shrink-0">
      <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
        <circle cx="18" cy="18" r="15.9155" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3.5" />
        <circle cx="18" cy="18" r="15.9155" fill="none" stroke={c} strokeWidth="3.5" strokeDasharray={`${pct}, ${100 - pct}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[11px] font-outfit font-bold text-white">{pct}%</div>
    </div>
  );
}

function ActiveDeploymentsCard({ items, loading }) {
  return (
    <div className="rounded-2xl bg-[#141414] border border-white/10 overflow-hidden" data-testid="dash-active">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-gold-500" />
          <div className="text-white font-outfit font-bold text-xs uppercase tracking-widest">Active Deployments</div>
        </div>
        <Link to="/company/bookings" className="text-xs text-brand font-outfit font-bold uppercase tracking-widest">View All</Link>
      </div>
      <div className="p-5">
        {loading ? <SkeletonRows /> : items.length === 0 ? (
          <div className="text-sm text-white/50 font-manrope italic py-4">No live deployments right now.</div>
        ) : (
          <ul className="space-y-3">
            {items.slice(0, 5).map((b, i) => (
              <li key={b.id || i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-b-0" data-testid={`active-row-${b.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">
                    {b.job_number || `JOB-${(b.id || "").slice(0, 4)}`}
                  </div>
                  <div className="text-sm text-white font-manrope truncate">{b.title || b.role || "Deployment"}</div>
                  <div className="text-[11px] text-white/50 font-manrope flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" /> {b.location || "Site TBC"} · {fmtTime(b.start_at)} – {fmtTime(b.end_at)}
                  </div>
                </div>
                <div className="text-center">
                  <ProgressRing value={progressPct(b)} tone={progressPct(b) >= 66 ? "gold" : progressPct(b) >= 33 ? "amber" : "brand"} />
                  <div className="text-[10px] text-white/50 mt-1 font-manrope">{Math.round(progressPct(b))}%</div>
                </div>
                <ChevronRight className="w-4 h-4 text-white/30" />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PlannedDeploymentsCard({ items, loading }) {
  const checklist = ["Confirm Team", "SOP Review", "Risk Assessment", "Client Briefing", "Equipment Check"];
  return (
    <div className="rounded-2xl bg-[#141414] border border-white/10 overflow-hidden" data-testid="dash-planned">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-violet-400" />
          <div className="text-white font-outfit font-bold text-xs uppercase tracking-widest">Planned Deployments</div>
        </div>
        <Link to="/company/bookings" className="text-xs text-brand font-outfit font-bold uppercase tracking-widest">View All</Link>
      </div>
      <div className="p-5">
        {loading ? <SkeletonRows /> : items.length === 0 ? (
          <div className="text-sm text-white/50 font-manrope italic py-4">No upcoming deployments scheduled.</div>
        ) : (
          <ul className="space-y-3">
            {items.slice(0, 5).map((b) => (
              <li key={b.id} className="flex items-start gap-3 py-2 border-b border-white/5 last:border-b-0" data-testid={`planned-row-${b.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">
                    {b.job_number || `JOB-${(b.id || "").slice(0, 4)}`}
                  </div>
                  <div className="text-sm text-white font-manrope truncate">{b.title || b.role || "Deployment"}</div>
                  <div className="text-[11px] text-white/50 font-manrope flex items-center gap-1 mt-0.5">
                    <Calendar className="w-3 h-3" /> {fmtDate(b.start_at)} · {fmtTime(b.start_at)} – {fmtTime(b.end_at)}
                  </div>
                </div>
                <div className="text-xs text-white/70 font-manrope space-y-1 min-w-[130px]">
                  {checklist.slice(0, 4).map((c, i) => (
                    <div key={c} className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full border ${i === 0 ? "border-brand bg-brand/30" : "border-white/25"}`} />
                      <span className={i === 0 ? "text-white" : "text-white/50"}>{c}</span>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function QuickStat({ Icon, label, value, sub, tone }) {
  const tones = {
    brand:  "text-brand",
    gold:   "text-gold-500",
    amber:  "text-amber-400",
    violet: "text-violet-300",
    cyan:   "text-cyan-300",
  };
  return (
    <div className="rounded-2xl bg-[#141414] border border-white/10 p-4 flex items-center gap-3">
      <Icon className={`w-6 h-6 ${tones[tone] || "text-white"}`} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-white/50 font-outfit font-bold leading-tight">{label}</div>
        <div className="text-2xl font-outfit font-bold text-white leading-tight mt-0.5">{value}</div>
        <div className="text-[10px] text-white/50 font-manrope leading-tight">{sub}</div>
      </div>
    </div>
  );
}

function EmergencyAlert() {
  return (
    <div className="rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-500/10 to-[#141414] p-4 flex flex-col justify-between" data-testid="dash-emergency">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-red-400 font-outfit font-bold mb-1">Need Immediate Assistance?</div>
        <div className="text-xs text-white/85 font-manrope leading-tight mb-3">Send an Emergency Alert to your teams now.</div>
      </div>
      <button className="w-full bg-red-500 hover:bg-red-600 text-white font-outfit font-bold uppercase tracking-widest text-xs rounded-md py-2.5 flex items-center justify-center gap-1.5" data-testid="dash-emergency-btn">
        <AlertTriangle className="w-4 h-4" /> Emergency Alert
      </button>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      {[1,2,3].map(i => (
        <div key={i} className="h-14 rounded-md bg-white/5 animate-pulse" />
      ))}
    </div>
  );
}

/* ---------- helpers ---------- */
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso); if (isNaN(+d)) return "—";
  return d.toLocaleDateString("en-IE", { day: "2-digit", month: "short" });
}
function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso); if (isNaN(+d)) return "—";
  return d.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function progressPct(b) {
  if (b.status === "completed") return 100;
  if (b.status === "in_progress" && b.start_at && b.end_at) {
    const s = +new Date(b.start_at), e = +new Date(b.end_at), n = Date.now();
    if (n <= s) return 0;
    if (n >= e) return 100;
    return Math.round(((n - s) / (e - s)) * 100);
  }
  return 45;
}
